import { useEffect, useRef } from "react";

/**
 * DOCUMENT FRAME — the real ATLAS note, rendered faithfully.
 *
 * The 39-page document is served by the backend as its own HTML (its own typography, layout and
 * inlined fonts). We render it in an iframe via `srcdoc` — which is same-origin, so the parent can
 * reach into `contentDocument`, anchor to the editable blocks (tagged `data-block-id` server-side),
 * scroll to one, highlight it, and apply an accepted edit live. No cross-frame message protocol is
 * needed, and the document's stylesheet is fully isolated from the app's, so neither can corrupt the
 * other. What the operator reads here is what the export produces.
 *
 * It NEVER invents content: it renders exactly the HTML it is handed. Applying an accepted edit only
 * ever rewrites the text of the one tagged block; Keep restores that block's original text verbatim.
 */

const FRAME_STYLE = `
  html { scroll-behavior: smooth; }
  [data-block-id] { transition: outline-color .2s ease, background-color .2s ease; border-radius: 6px; }
  [data-block-id][data-studio-suggested="true"] { cursor: pointer; background: rgba(255, 191, 71, .10); outline: 2px solid transparent; outline-offset: 3px; }
  [data-block-id][data-studio-focused="true"] { background: rgba(0, 113, 227, .10); outline-color: rgba(0, 113, 227, .55); }
  [data-block-id][data-studio-applied="true"] { background: rgba(52, 168, 83, .10); }
  [data-block-id][data-studio-editable="true"] { cursor: text; }
  [data-block-id][data-studio-editable="true"]:hover { outline: 1px dashed rgba(0, 113, 227, .40); outline-offset: 3px; }
  [data-block-id][data-editing="true"] { outline: 2px solid rgba(0, 113, 227, .55); outline-offset: 3px; background: rgba(0, 113, 227, .06); }
  @media (prefers-reduced-motion: reduce) {
    html { scroll-behavior: auto; }
    [data-block-id] { transition: none; }
  }
`;

const SCROLL_BEHAVIOR: ScrollBehavior =
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    ? "auto"
    : "smooth";

export function DocumentFrame({
  html,
  suggestedBlockIds,
  editableBlockIds,
  focusedBlockId,
  appliedTexts,
  onBlockFocus,
  onBlockEdit,
  onReady
}: {
  html: string;
  /** Blocks that carry a suggestion — they get the readable "suggested" ground + pointer. */
  suggestedBlockIds: string[];
  /** Blocks the operator may type into directly (the declared body blocks; locked blocks are inert). */
  editableBlockIds: string[];
  /** The block currently in focus — scrolled into view and ringed. */
  focusedBlockId: string | null;
  /** blockId -> accepted text to write into that block, or null to restore the original. */
  appliedTexts: Record<string, string | null>;
  onBlockFocus: (blockId: string) => void;
  /** Fired on blur when the operator has directly edited a block's text. */
  onBlockEdit: (blockId: string, text: string) => void;
  onReady: (blockIds: string[]) => void;
}) {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const readyRef = useRef(false);
  const originalHtmlRef = useRef<Record<string, string>>({});
  // Keep the latest callbacks without re-binding listeners on every render.
  const onBlockFocusRef = useRef(onBlockFocus);
  onBlockFocusRef.current = onBlockFocus;
  const onBlockEditRef = useRef(onBlockEdit);
  onBlockEditRef.current = onBlockEdit;
  const appliedTextsRef = useRef(appliedTexts);
  appliedTextsRef.current = appliedTexts;

  function docOf() {
    return frameRef.current?.contentDocument ?? null;
  }

  function handleLoad() {
    const doc = docOf();
    if (!doc) return;
    // Isolate the app's chrome from the document: inject only our small interaction style.
    const style = doc.createElement("style");
    style.textContent = FRAME_STYLE;
    doc.head?.appendChild(style);

    const editable = new Set(editableBlockIds);
    const blocks = Array.from(doc.querySelectorAll<HTMLElement>("[data-block-id]"));
    const ids: string[] = [];
    for (const block of blocks) {
      const id = block.getAttribute("data-block-id") || "";
      if (!id || id in originalHtmlRef.current) continue;
      ids.push(id);
      originalHtmlRef.current[id] = block.innerHTML;
      block.addEventListener("click", () => onBlockFocusRef.current(id));
      // Direct editing: only the declared body blocks are typeable; every other block, and the
      // whole rest of the 39-page document, stays inert. Plaintext-only so on-screen bytes equal
      // exported bytes (the locator places plain text). While a block is being edited we STOP
      // syncing it (below), so the caret and protected chips are never wiped mid-keystroke; the
      // text is serialized into a change only on blur.
      if (editable.has(id)) {
        block.setAttribute("contenteditable", "plaintext-only");
        block.setAttribute("data-studio-editable", "true");
        block.addEventListener("focus", () => block.setAttribute("data-editing", "true"));
        block.addEventListener("blur", () => {
          block.setAttribute("data-editing", "false");
          const text = (block.textContent || "").replace(/\s+/g, " ").trim();
          const original = (originalHtmlRef.current[id] || "").replace(/<[^>]+>/g, "").replace(/\s+/g, " ").trim();
          const applied = appliedTextsRef.current[id];
          const current = applied != null ? applied.replace(/\s+/g, " ").trim() : original;
          if (text && text !== current) onBlockEditRef.current(id, text);
        });
      }
    }
    readyRef.current = true;
    onReady(ids);
    // Paint the initial suggested/applied/focused state now that the doc exists.
    syncBlockState();
  }

  function syncBlockState() {
    const doc = docOf();
    if (!doc || !readyRef.current) return;
    const suggested = new Set(suggestedBlockIds);
    doc.querySelectorAll<HTMLElement>("[data-block-id]").forEach((block) => {
      const id = block.getAttribute("data-block-id") || "";
      block.setAttribute("data-studio-suggested", suggested.has(id) ? "true" : "false");
      block.setAttribute("data-studio-focused", id === focusedBlockId ? "true" : "false");
      // NEVER rewrite a block the operator is actively editing — that would wipe the caret and any
      // protected chips mid-keystroke. The live DOM is the source of truth until blur commits it.
      if (block.getAttribute("data-editing") === "true") return;
      const applied = appliedTexts[id];
      if (applied != null) {
        block.textContent = applied;
        block.setAttribute("data-studio-applied", "true");
      } else if (originalHtmlRef.current[id] != null) {
        block.innerHTML = originalHtmlRef.current[id];
        block.setAttribute("data-studio-applied", "false");
      }
    });
  }

  // Repaint suggested/applied/focused whenever the inputs change.
  useEffect(() => {
    syncBlockState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedBlockIds, focusedBlockId, appliedTexts]);

  // Scroll the focused block to the middle of the document viewport. We compute the target
  // scrollTop against the iframe's OWN scrolling element rather than call scrollIntoView, which
  // behaves inconsistently across the iframe boundary (it often nudges the parent to reveal the
  // whole frame instead of scrolling the note to the paragraph). Deferred one frame so it measures
  // after syncBlockState has repainted the focused/applied state.
  useEffect(() => {
    const doc = docOf();
    if (!doc || !readyRef.current || !focusedBlockId) return;
    const target = doc.querySelector<HTMLElement>(`[data-block-id="${CSS.escape(focusedBlockId)}"]`);
    if (!target) return;
    const raf = frameRef.current?.contentWindow?.requestAnimationFrame ?? window.requestAnimationFrame;
    const handle = raf(() => {
      const scroller = (doc.scrollingElement || doc.documentElement) as HTMLElement | null;
      if (!scroller) return;
      const rect = target.getBoundingClientRect();
      const top = scroller.scrollTop + rect.top - scroller.clientHeight / 2 + rect.height / 2;
      scroller.scrollTo({ top: Math.max(0, top), behavior: SCROLL_BEHAVIOR });
    });
    return () => (frameRef.current?.contentWindow?.cancelAnimationFrame ?? window.cancelAnimationFrame)(handle);
  }, [focusedBlockId]);

  return (
    <iframe
      ref={frameRef}
      className="studio-frame"
      title="ATLAS document"
      srcDoc={html}
      onLoad={handleLoad}
      // The document is our own backend HTML rendered same-origin; it runs no scripts of its own.
      sandbox="allow-same-origin"
    />
  );
}
